<style>
    .inventory-table :global(th),
    .inventory-table :global(td) {
        border-width: 0;
        border-bottom: 1px solid #e2e8f0;
    }

    .inventory-table :global(tbody tr:last-child td) {
        border-bottom-width: 0;
    }

    .secondary-button {
        background-color: #edf2f7;
        color: #4a5568;
    }

    .secondary-button:hover {
        background-color: #e2e8f0;
    }

    .tab-list {
        display: flex;
        gap: 0.25rem;
        overflow-x: auto;
        border-bottom: 1px solid #e2e8f0;
    }

    .tab-button {
        flex: 0 0 auto;
        border-radius: 0.25rem 0.25rem 0 0;
        background-color: transparent;
        color: #718096;
        padding: 0.375rem 0.75rem;
        white-space: nowrap;
    }

    .tab-button:hover,
    .tab-button.active {
        background-color: #edf2f7;
        color: #2d3748;
    }

    .tab-button.active {
        box-shadow: inset 0 -2px #4299e1;
    }

    .tab-button:focus-visible {
        outline: 2px solid #4299e1;
        outline-offset: -2px;
    }

    .value-positive {
        background-color: #f0fff4;
    }

    .value-negative {
        background-color: #fff5f5;
    }

    .value-zero {
        background-color: #fefcbf;
    }

    select {
        border: 1px solid #cbd5e0;
        border-radius: 0.25rem;
        padding: 0.25rem 0.5rem;
    }
</style>

<script>
    import { onMount } from 'svelte';

    import { buildSearch } from '../../utils';
    import {
        ajustesInventario,
        cancelarAjusteInventario,
        guardarAjustesInventario,
        productos,
        traerAjustesInventario,
        traerProductos,
    } from './_productos.svelte';

    const motivos = [
        ['conteo', 'Conteo físico'],
        ['danado', 'Dañado o perdido'],
        ['otro', 'Otro'],
    ];

    let cantidades = {};
    let motivosPorProducto = {};
    let filtro = '';
    let pestanaActiva = 'inventario';
    let guardando = false;
    let mensaje = '';
    let error = '';

    $: filtroRe = buildSearch(filtro);
    $: filas = $productos
        .filter(({ slug }) => slug.match(filtroRe))
        .sort(
            (productoA, productoB) =>
                Number(productoB.cantidad !== 0) -
                Number(productoA.cantidad !== 0)
        )
        .map(producto => {
            const valor = cantidades[producto.slug];
            const tieneConteo =
                valor !== undefined && valor !== null && valor !== '';
            const cantidadContada = tieneConteo ? Number(valor) : null;
            const cantidadValida =
                cantidadContada === null ||
                (Number.isInteger(cantidadContada) && cantidadContada >= 0);

            return {
                ...producto,
                cantidadContada,
                cantidadValida,
                diferencia:
                    cantidadContada === null || !cantidadValida
                        ? null
                        : cantidadContada - producto.cantidad,
            };
        });
    $: cambios = filas
        .filter(({ diferencia }) => diferencia !== null && diferencia !== 0)
        .map(({ slug, cantidadContada }) => ({
            slug,
            cantidad_contada: cantidadContada,
            motivo: motivosPorProducto[slug] || 'conteo',
        }));
    $: tieneCantidadInvalida = filas.some(
        ({ cantidadValida }) => !cantidadValida
    );

    function mostrarDiferencia(diferencia) {
        if (diferencia === null) return '—';
        return diferencia > 0 ? `+${diferencia}` : diferencia;
    }

    function mostrarFecha(fecha) {
        return new Date(fecha).toLocaleString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    async function guardar() {
        guardando = true;
        mensaje = '';
        error = '';
        try {
            const { creados } = await guardarAjustesInventario(cambios);
            cantidades = {};
            motivosPorProducto = {};
            mensaje = `${creados} ${
                creados === 1 ? 'corrección guardada' : 'correcciones guardadas'
            }.`;
        } catch (err) {
            error =
                'No se pudieron guardar las correcciones. Intenta de nuevo.';
        } finally {
            guardando = false;
        }
    }

    async function cancelar(ajuste) {
        const confirmado = window.confirm(
            `¿Cancelar la corrección de ${ajuste.producto}?`
        );
        if (!confirmado) return;

        mensaje = '';
        error = '';
        try {
            await cancelarAjusteInventario(ajuste.id);
            mensaje = 'La corrección fue cancelada.';
        } catch (err) {
            error = 'No se pudo cancelar la corrección. Intenta de nuevo.';
        }
    }

    onMount(() => Promise.all([traerProductos(), traerAjustesInventario()]));
</script>

<svelte:head>
    <title>Actualizar inventario</title>
</svelte:head>

<main class="py-6">
    <div class="max-w-6xl px-4 mx-auto sm:px-6 lg:px-8">
        <div class="mb-6">
            <h1 class="text-2xl font-semibold tracking-tight">
                Actualizar inventario
            </h1>
            <p class="max-w-2xl mt-2 text-base text-gray-600 sm:text-sm">
                Escribe la cantidad que contaste. La diferencia se aplica al
                inventario cuando guardas los cambios.
            </p>
        </div>

        <nav
            class="mb-6 tab-list"
            role="tablist"
            aria-label="Secciones de inventario"
        >
            <button
                id="tab-inventario"
                class="tab-button"
                class:active="{pestanaActiva === 'inventario'}"
                type="button"
                variant="secondary"
                role="tab"
                aria-selected="{pestanaActiva === 'inventario'}"
                aria-controls="panel-inventario"
                on:click="{() => (pestanaActiva = 'inventario')}"
            >
                Inventario
            </button>
            <button
                id="tab-correcciones"
                class="tab-button"
                class:active="{pestanaActiva === 'correcciones'}"
                type="button"
                variant="secondary"
                role="tab"
                aria-selected="{pestanaActiva === 'correcciones'}"
                aria-controls="panel-correcciones"
                on:click="{() => (pestanaActiva = 'correcciones')}"
            >
                Correcciones recientes
            </button>
        </nav>

        {#if mensaje}
            <p
                class="p-3 mb-4 text-base text-green-800 bg-green-100 rounded
                sm:text-sm"
            >
                {mensaje}
            </p>
        {/if}
        {#if error}
            <p
                class="p-3 mb-4 text-base text-red-800 bg-red-100 rounded
                sm:text-sm"
            >
                {error}
            </p>
        {/if}

        {#if pestanaActiva === 'inventario'}
            <section
                id="panel-inventario"
                role="tabpanel"
                aria-labelledby="tab-inventario"
            >

                <div
                    class="flex flex-col mb-4 sm:flex-row sm:items-end
                    sm:justify-between"
                >
                    <div class="mb-3 sm:mb-0">
                        <label
                            class="text-base font-medium sm:text-sm"
                            for="filtro-producto"
                        >
                            Buscar producto
                        </label>
                        <input
                            id="filtro-producto"
                            name="filtro-producto"
                            class="w-full mt-1 text-base sm:w-64 sm:text-sm"
                            type="search"
                            bind:value="{filtro}"
                        />
                    </div>
                    <button
                        type="button"
                        disabled="{guardando || cambios.length === 0 || tieneCantidadInvalida}"
                        on:click="{guardar}"
                    >
                        {guardando ? 'Guardando…' : `Guardar cambios${cambios.length ? ` (${cambios.length})` : ''}`}
                    </button>
                </div>

                <div class="-mx-4 overflow-x-auto whitespace-no-wrap sm:mx-0">
                    <div
                        class="inline-block min-w-full px-4 align-middle sm:px-0"
                    >
                        <table class="w-full inventory-table">
                            <thead>
                                <tr class="text-left text-gray-600">
                                    <th class="whitespace-no-wrap">Producto</th>
                                    <th class="text-right whitespace-no-wrap">
                                        Cantidad del sistema
                                    </th>
                                    <th class="text-right whitespace-no-wrap">
                                        Cantidad contada
                                    </th>
                                    <th class="text-right whitespace-no-wrap">
                                        Diferencia
                                    </th>
                                    <th class="whitespace-no-wrap">Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each filas as fila (fila.slug)}
                                    <tr>
                                        <td class="font-medium">
                                            {fila.nombre}
                                        </td>
                                        <td
                                            class="text-right tabular-nums"
                                            class:value-positive="{fila.cantidad > 0}"
                                            class:value-negative="{fila.cantidad < 0}"
                                            class:value-zero="{fila.cantidad === 0}"
                                        >
                                            {fila.cantidad}
                                        </td>
                                        <td class="text-right">
                                            <input
                                                aria-label="{`Cantidad contada de ${fila.nombre}`}"
                                                name="{`cantidad-${fila.slug}`}"
                                                class="w-24 text-base text-right
                                                tabular-nums sm:text-sm"
                                                class:border-red-500="{!fila.cantidadValida}"
                                                type="number"
                                                min="0"
                                                step="1"
                                                bind:value="{cantidades[fila.slug]}"
                                            />
                                        </td>
                                        <td
                                            class="text-right tabular-nums"
                                            class:value-positive="{fila.diferencia > 0}"
                                            class:value-negative="{fila.diferencia < 0}"
                                            class:value-zero="{fila.diferencia === 0}"
                                            class:text-red-700="{fila.diferencia < 0}"
                                            class:text-green-700="{fila.diferencia > 0}"
                                        >
                                            {mostrarDiferencia(fila.diferencia)}
                                        </td>
                                        <td>
                                            <select
                                                aria-label="{`Motivo del ajuste de ${fila.nombre}`}"
                                                name="{`motivo-${fila.slug}`}"
                                                class="text-base bg-white
                                                sm:text-sm"
                                                value="{motivosPorProducto[fila.slug] || 'conteo'}"
                                                on:change="{event => {
                                                    motivosPorProducto[fila.slug] = event.target.value;
                                                    motivosPorProducto = motivosPorProducto;
                                                }}"
                                            >
                                                {#each motivos as [valor, etiqueta]}
                                                    <option value="{valor}">
                                                        {etiqueta}
                                                    </option>
                                                {/each}
                                            </select>
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        {:else}
            <section
                id="panel-correcciones"
                role="tabpanel"
                aria-labelledby="tab-correcciones"
            >
                <p class="max-w-2xl text-base text-gray-600 sm:text-sm">
                    Cancela una corrección si la cantidad contada fue
                    incorrecta.
                </p>

                {#if $ajustesInventario.length}
                    <div
                        class="-mx-4 mt-4 overflow-x-auto whitespace-no-wrap
                        sm:mx-0"
                    >
                        <div
                            class="inline-block min-w-full px-4 align-middle
                            sm:px-0"
                        >
                            <table class="w-full inventory-table">
                                <thead>
                                    <tr class="text-left text-gray-600">
                                        <th class="whitespace-no-wrap">
                                            Fecha
                                        </th>
                                        <th class="whitespace-no-wrap">
                                            Producto
                                        </th>
                                        <th
                                            class="text-right whitespace-no-wrap"
                                        >
                                            Sistema
                                        </th>
                                        <th
                                            class="text-right whitespace-no-wrap"
                                        >
                                            Contada
                                        </th>
                                        <th
                                            class="text-right whitespace-no-wrap"
                                        >
                                            Diferencia
                                        </th>
                                        <th class="whitespace-no-wrap">
                                            Motivo
                                        </th>
                                        <th>
                                            <span class="sr-only">
                                                Acciones
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each $ajustesInventario as ajuste (ajuste.id)}
                                        <tr
                                            class:opacity-50="{ajuste.cancelado}"
                                        >
                                            <td>
                                                {mostrarFecha(ajuste.creado_en)}
                                            </td>
                                            <td class="font-medium">
                                                {ajuste.producto}
                                            </td>
                                            <td class="text-right tabular-nums">
                                                {ajuste.cantidad_sistema}
                                            </td>
                                            <td class="text-right tabular-nums">
                                                {ajuste.cantidad_contada}
                                            </td>
                                            <td
                                                class="text-right tabular-nums"
                                                class:value-positive="{ajuste.diferencia > 0 && !ajuste.cancelado}"
                                                class:value-negative="{ajuste.diferencia < 0 && !ajuste.cancelado}"
                                                class:value-zero="{ajuste.diferencia === 0 && !ajuste.cancelado}"
                                                class:text-red-700="{ajuste.diferencia < 0 && !ajuste.cancelado}"
                                                class:text-green-700="{ajuste.diferencia > 0 && !ajuste.cancelado}"
                                            >
                                                {mostrarDiferencia(ajuste.diferencia)}
                                            </td>
                                            <td>{ajuste.motivo_descripcion}</td>
                                            <td class="text-right">
                                                {#if ajuste.cancelado}
                                                    <span>Cancelada</span>
                                                {:else}
                                                    <button
                                                        type="button"
                                                        variant="secondary"
                                                        class="secondary-button"
                                                        on:click="{() => cancelar(ajuste)}"
                                                    >
                                                        Cancelar corrección
                                                    </button>
                                                {/if}
                                            </td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    </div>
                {:else}
                    <p class="mt-4 text-base text-gray-600 sm:text-sm">
                        Todavía no hay correcciones.
                    </p>
                {/if}
            </section>
        {/if}
    </div>
</main>
